import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Upload, Check, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import Footer from "../components/Footer";
import BrandMark from "../components/BrandMark";

const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT = ".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation";

export default function DeckUpload() {
  const nav = useNavigate();
  const { id } = useParams();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(null); // {status, deck_quality_score}
  const [error, setError] = useState("");

  const onFile = (e) => {
    setError("");
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().split(".").pop();
    if (!["pdf", "pptx"].includes(ext)) {
      setError("Разрешены только PDF и PPTX");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`Файл больше 50 МБ (${Math.round(f.size / 1024 / 1024)} МБ)`);
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/api/submissions/${id}/deck`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        timeout: 120_000,
      });
      setDone(data.deck);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const proceed = () => nav(`/loading/${id}`);
  const skip = () => nav(`/loading/${id}`);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="border-b border-black/10">
        <div className="max-w-screen-xl mx-auto px-8 py-5"><BrandMark /></div>
      </div>
      <main className="flex-1">
        <div className="max-w-screen-xl mx-auto px-8 py-16 md:py-24 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="label-mono">ШАГ · ПРЕЗЕНТАЦИЯ</div>
            <h1 className="mt-4 text-4xl md:text-5xl font-medium tracking-tight">
              Презентация<br />(опционально)
            </h1>
            <p className="mt-4 text-foreground/70 leading-relaxed">
              Загрузите pitch deck (PDF или PPTX до 50&nbsp;МБ) — аналитическая модель
              извлечёт структурированные факты о&nbsp;проекте и&nbsp;даст дополнительный
              сигнал о&nbsp;качестве&nbsp;дека. Это увеличивает точность диагностики.
            </p>
            <p className="mt-4 text-xs text-foreground/55 leading-relaxed">
              Можно пропустить — индексы всё равно будут рассчитаны по&nbsp;ответам анкеты.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6">
            {done ? (
              <div className="grid-cell" data-testid="deck-uploaded-card">
                <div className="flex items-center gap-3 label-mono">
                  <Check size={16} /> ЗАГРУЖЕНО
                </div>
                <h3 className="mt-3 text-xl font-medium">{done.filename}</h3>
                <div className="mt-2 text-sm text-foreground/70 mono">
                  {done.format?.toUpperCase()} · {done.pages} стр · {Math.round((done.size_bytes || 0) / 1024)} КБ
                </div>
                <div className="mt-2 text-sm text-foreground/70 mono">
                  AI статус: <span className="text-foreground">{done.extraction_status}</span>
                  {typeof done.deck_quality_score === "number" && done.extraction_status === "ok" && (
                    <> · deck_quality {done.deck_quality_score}/5</>
                  )}
                </div>
                <div className="pt-6 flex gap-3">
                  <button className="btn-primary" onClick={proceed} data-testid="deck-continue-btn">
                    К результату <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <label
                  className={`grid-cell flex flex-col items-center justify-center gap-3 cursor-pointer border-dashed ${file ? "border-foreground" : ""}`}
                  data-testid="deck-dropzone"
                  style={{ minHeight: 180 }}
                >
                  <input
                    type="file"
                    accept={ACCEPT}
                    onChange={onFile}
                    className="hidden"
                    data-testid="deck-file-input"
                  />
                  <Upload size={20} />
                  <div className="text-sm text-center">
                    {file ? (
                      <>
                        <span className="font-medium">{file.name}</span>
                        <span className="block text-foreground/60 mono text-xs mt-1">
                          {Math.round(file.size / 1024 / 1024 * 10) / 10} МБ
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-medium">Выберите файл</span>
                        <span className="block text-foreground/60 mono text-xs mt-1">PDF или PPTX · до 50 МБ</span>
                      </>
                    )}
                  </div>
                </label>

                {file && !uploading && (
                  <div className="flex items-center gap-2 text-xs label-mono">
                    <button className="hop-link" onClick={() => setFile(null)} data-testid="deck-clear-btn">
                      <X size={12} className="inline" /> Убрать
                    </button>
                  </div>
                )}

                {uploading && (
                  <div data-testid="deck-progress">
                    <div className="label-mono text-xs">ЗАГРУЗКА · {progress}%</div>
                    <div className="h-1 bg-muted mt-2">
                      <div className="h-full bg-foreground transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                )}

                {error && <p className="text-destructive label-mono" data-testid="deck-error">{error}</p>}

                <div className="flex flex-wrap gap-3 pt-4 border-t border-black/10">
                  <button className="btn-ghost" onClick={skip} data-testid="deck-skip-btn" disabled={uploading}>
                    Пропустить
                  </button>
                  <button
                    className="btn-primary"
                    disabled={!file || uploading}
                    onClick={upload}
                    data-testid="deck-upload-btn"
                  >
                    {uploading ? `Загружаем… ${progress}%` : "Загрузить и анализировать"} <ArrowRight size={16} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
